// src/screens/AdminGameDemandScreen.js
// ============================================================================
// ADMIN: GAME DEMAND (founder 2026-07-10) — what streamers set as their game,
// rolled up as a trending view. Two lists from adminGetGameDemand:
//   - Catalog games: per-key counts across streamProfiles
//   - "Other": the free-text names streamers typed, grouped + counted
// This is the founder's selection signal for future event / test-event games
// (the "test events = vision bootstrap" strategy).
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import { streamGameName } from '../constants/streamGames';

const COLORS = {
  BG: '#0b0b0b', CARD: '#111', CARD_BORDER: '#222',
  GOLD: '#FCD000', TEXT: '#EDEDED', DIM: '#888',
};

const gameName = (key) => (key ? streamGameName(key) : '');

export default function AdminGameDemandScreen({ navigation }) {
  const functions = getFunctions();
  const [data, setData] = useState(null);
  const [tally, setTally] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'adminGetGameDemand');
      const tallyFn = httpsCallable(functions, 'adminGetGameTally');
      const [result, tallyResult] = await Promise.all([
        fn(),
        // Tally is additive — the census still renders if it fails.
        tallyFn().catch(() => null),
      ]);
      setData(result.data || null);
      setTally(tallyResult?.data || null);
    } catch (err) {
      setError(err.message || 'Could not load game demand.');
    } finally {
      setLoading(false);
    }
  }, [functions]);

  useEffect(() => { load(); }, [load]);

  const maxCatalog = Math.max(1, ...(data?.catalog || []).map((g) => g.count));
  const maxCustom = Math.max(1, ...(data?.custom || []).map((g) => g.count));

  const barRow = (label, count, max, highlight) => (
    <View key={label} style={styles.barRow}>
      <Text style={[styles.barLabel, highlight && { color: COLORS.GOLD }]} numberOfLines={1}>{label}</Text>
      <View style={styles.barTrack}>
        <View style={[styles.barFill, { width: `${Math.max(4, (count / max) * 100)}%` }, highlight && { backgroundColor: COLORS.GOLD }]} />
      </View>
      <Text style={styles.barCount}>{count}</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.titleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>Game Demand</Text>
            <Text style={styles.subtitle}>
              What streamers set as their game — your signal for picking the next event and test-event games.
            </Text>
          </View>
          <TouchableOpacity style={styles.refreshBtn} onPress={load} disabled={loading}>
            <Ionicons name="refresh" size={16} color={COLORS.GOLD} />
          </TouchableOpacity>
        </View>

        {loading ? (
          <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 60 }} />
        ) : error ? (
          <Text style={styles.errorText}>{error}</Text>
        ) : !data ? null : (
          <>
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Catalog games</Text>
              <Text style={styles.cardSub}>{data.profilesScanned} stream profile{data.profilesScanned === 1 ? '' : 's'} scanned</Text>
              {data.catalog.length ? (
                <View style={{ marginTop: 12 }}>
                  {data.catalog.map((g) => barRow(gameName(g.game), g.count, maxCatalog, false))}
                </View>
              ) : (
                <Text style={styles.emptyText}>No catalog games selected yet.</Text>
              )}
            </View>

            <View style={styles.card}>
              <Text style={styles.cardTitle}>"Other" — what streamers typed</Text>
              <Text style={styles.cardSub}>
                Games outside the catalog, grouped by name. High counts here = candidates for the next test event.
              </Text>
              {data.custom.length ? (
                <View style={{ marginTop: 12 }}>
                  {data.custom.map((g) => barRow(g.name, g.count, maxCustom, true))}
                </View>
              ) : (
                <Text style={styles.emptyText}>Nobody has picked "Other" yet.</Text>
              )}
              {data.otherUnspecified > 0 ? (
                <Text style={styles.footnote}>
                  +{data.otherUnspecified} picked "Other" without typing a name.
                </Text>
              ) : null}
            </View>

            <View style={styles.card}>
              <Text style={styles.cardTitle}>All-time tally</Text>
              <Text style={styles.cardSub}>
                Running totals per game — sessions streamed, minutes, events posted, entries, leagues.
                Survives profile edits. Sort order = total activity; games without a full profile are
                the vision-adaptation queue.
              </Text>
              {tally?.rows?.length ? (
                <View style={{ marginTop: 12 }}>
                  {tally.rows.map((r) => (
                    <View key={r.gameKey} style={styles.tallyRow}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.tallyName} numberOfLines={1}>
                          {r.displayName}
                          {r.catalogTier !== 'full' ? (
                            <Text style={styles.tallyBadge}>  {r.catalogTier === 'community' ? 'COMMUNITY' : 'NO PROFILE'}</Text>
                          ) : null}
                        </Text>
                        <Text style={styles.tallyDetail}>
                          {r.counts.streams} streams · {r.counts.streamMinutes} min · {r.counts.events} events · {r.counts.entries} entries · {r.counts.leagues} leagues
                        </Text>
                      </View>
                      <Text style={styles.tallyTotal}>{r.total}</Text>
                    </View>
                  ))}
                </View>
              ) : (
                <Text style={styles.emptyText}>No tallied activity yet — counts start with the next stream or event.</Text>
              )}
            </View>

            <TouchableOpacity style={styles.backButton} onPress={() => navigation.navigate('AdminDashboard')}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="arrow-back" size={18} color="#888" style={{ marginRight: 4 }} />
                <Text style={styles.backButtonText}>Back to Admin Dashboard</Text>
              </View>
            </TouchableOpacity>
          </>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.BG },
  content: { padding: 16, paddingBottom: 100, maxWidth: 760, width: '100%', alignSelf: 'center' },
  titleRow: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 8 },
  title: { color: '#fff', fontSize: 22, fontWeight: '800' },
  subtitle: { color: COLORS.DIM, fontSize: 13, marginTop: 4, lineHeight: 19 },
  refreshBtn: {
    backgroundColor: '#1a1604', borderColor: '#4a3d00', borderWidth: 1,
    borderRadius: 8, padding: 9, marginLeft: 10,
  },
  errorText: { color: '#e05555', fontSize: 13, textAlign: 'center', marginTop: 40 },
  card: {
    backgroundColor: COLORS.CARD, borderColor: COLORS.CARD_BORDER, borderWidth: 1,
    borderRadius: 12, padding: 16, marginTop: 16,
  },
  cardTitle: { color: '#fff', fontSize: 15, fontWeight: '800' },
  cardSub: { color: COLORS.DIM, fontSize: 12, marginTop: 4, lineHeight: 17 },
  emptyText: { color: '#777', fontSize: 12.5, marginTop: 14 },
  footnote: { color: '#777', fontSize: 11.5, marginTop: 12 },
  barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  barLabel: { width: 170, color: '#ccc', fontSize: 12.5, fontWeight: '600', marginRight: 10 },
  barTrack: { flex: 1, height: 10, borderRadius: 5, backgroundColor: '#1c1c1c', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 5, backgroundColor: '#3DD6F5' },
  barCount: { width: 40, color: '#fff', fontSize: 12.5, fontWeight: '800', textAlign: 'right' },
  tallyRow: {
    flexDirection: 'row', alignItems: 'center', marginBottom: 10,
    borderBottomColor: '#1a1a1a', borderBottomWidth: 1, paddingBottom: 8,
  },
  tallyName: { color: '#ccc', fontSize: 13, fontWeight: '700' },
  tallyBadge: { color: '#3DD6F5', fontSize: 10, fontWeight: '800' },
  tallyDetail: { color: '#777', fontSize: 11.5, marginTop: 2 },
  tallyTotal: { color: '#fff', fontSize: 15, fontWeight: '800', marginLeft: 12 },
  backButton: { marginTop: 22, alignSelf: 'flex-start' },
  backButtonText: { color: '#888', fontSize: 14 },
});
